import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Header } from '@/components/Header';
import { Footer } from '@/components/Footer';
import { toast } from 'sonner';
import { User, KeyRound, Mail, ArrowLeft } from 'lucide-react';
import { useAuth } from '@/hooks/useAuth';

// Password validation matching server-side requirements (10 chars, upper, lower, number)
const passwordSchema = z.string()
  .min(10, 'Lösenordet måste vara minst 10 tecken')
  .regex(/[A-ZÅÄÖ]/, 'Måste innehålla minst en stor bokstav')
  .regex(/[a-zåäö]/, 'Måste innehålla minst en liten bokstav')
  .regex(/[0-9]/, 'Måste innehålla minst en siffra');

const signupSchema = z.object({
  email: z.string().email('Ogiltig e-postadress'),
  password: passwordSchema,
  displayName: z.string().min(2, 'Visningsnamn måste vara minst 2 tecken').max(50, 'Visningsnamn får vara max 50 tecken'),
  gdprConsent: z.literal(true, { errorMap: () => ({ message: 'Du måste godkänna villkoren' }) }),
});

const loginSchema = z.object({
  email: z.string().email('Ogiltig e-postadress'),
  password: z.string().min(1, 'Ange lösenord'),
});

const resetRequestSchema = z.object({
  email: z.string().email('Ogiltig e-postadress'),
});

const resetPasswordSchema = z.object({
  password: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: 'Lösenorden matchar inte',
  path: ['confirmPassword'],
});

type AuthView = 'login' | 'signup' | 'forgot' | 'reset';

export default function Auth() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated, login, register, requestPasswordReset, resetPassword, resendConfirmation } = useAuth();
  
  const resetToken = searchParams.get('reset');
  
  const [view, setView] = useState<AuthView>(resetToken ? 'reset' : 'login');
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    displayName: '',
    gdprConsent: false,
    rememberMe: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showResendEmail, setShowResendEmail] = useState(false);

  useEffect(() => {
    if (isAuthenticated && view !== 'reset') {
      navigate('/forum');
    }
  }, [isAuthenticated, navigate, view]);

  useEffect(() => {
    if (resetToken) {
      setView('reset');
    }
  }, [resetToken]);

  const handleLogin = async () => {
    const result = loginSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const { error } = await login(formData.email, formData.password, formData.rememberMe);
    if (error) {
      if (error.includes('Email not confirmed') || error.includes('email_not_confirmed')) {
        setShowResendEmail(true);
        toast.error('Du måste bekräfta din e-post innan du kan logga in.');
        return;
      }
      toast.error(error);
      return;
    }

    toast.success('Inloggad!');
    navigate('/forum');
  };

  const handleResendConfirmation = async () => {
    if (!formData.email) {
      toast.error('Ange din e-postadress först');
      return;
    }
    setLoading(true);
    const { error } = await resendConfirmation(formData.email);
    setLoading(false);
    if (error) {
      toast.error(error);
    } else {
      toast.success('Bekräftelsemail skickat! Kolla din inkorg och skräppost.');
    }
  };

  const handleSignup = async () => {
    const result = signupSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const { error } = await register(formData.email, formData.password, formData.displayName);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Konto skapat!');
    navigate('/forum');
  };

  const handleForgotPassword = async () => {
    const result = resetRequestSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    const { error } = await requestPasswordReset(formData.email);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Om e-postadressen finns i systemet skickas en återställningslänk.');
    setView('login');
  };

  const handleResetPassword = async () => {
    const result = resetPasswordSchema.safeParse(formData);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) fieldErrors[err.path[0] as string] = err.message;
      });
      setErrors(fieldErrors);
      return;
    }

    if (!resetToken) {
      toast.error('Ogiltig återställningslänk');
      return;
    }

    const { error } = await resetPassword(formData.password);
    if (error) {
      toast.error(error);
      return;
    }

    toast.success('Lösenordet har ändrats! Du kan nu logga in.');
    navigate('/auth');
    setView('login');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    setLoading(true);

    try {
      switch (view) {
        case 'login':
          await handleLogin();
          break;
        case 'signup':
          await handleSignup();
          break;
        case 'forgot':
          await handleForgotPassword();
          break;
        case 'reset':
          await handleResetPassword();
          break;
      }
    } catch (error) {
      toast.error('Något gick fel. Försök igen.');
    } finally {
      setLoading(false);
    }
  };

  const getViewConfig = () => {
    switch (view) {
      case 'login':
        return {
          icon: User,
          title: 'Logga in',
          description: 'Logga in för att delta i forumet',
          buttonText: 'Logga in',
        };
      case 'signup':
        return {
          icon: User,
          title: 'Skapa konto',
          description: 'Skapa ett konto för att delta i diskussioner',
          buttonText: 'Skapa konto',
        };
      case 'forgot':
        return {
          icon: Mail,
          title: 'Glömt lösenord',
          description: 'Ange din e-post för att få en återställningslänk',
          buttonText: 'Skicka återställningslänk',
        };
      case 'reset':
        return {
          icon: KeyRound,
          title: 'Nytt lösenord',
          description: 'Ange ditt nya lösenord',
          buttonText: 'Spara nytt lösenord',
        };
    }
  };

  const config = getViewConfig();
  const Icon = config.icon;

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8 max-w-md flex items-center justify-center">
        <Card className="w-full">
          <CardHeader className="text-center">
            <div className="mx-auto w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Icon className="h-6 w-6 text-primary" />
            </div>
            <CardTitle className="font-display text-2xl">{config.title}</CardTitle>
            <CardDescription>{config.description}</CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {view === 'signup' && (
                <div className="space-y-2">
                  <Label htmlFor="displayName">Visningsnamn</Label>
                  <Input
                    id="displayName"
                    type="text"
                    placeholder="Ditt namn i forumet"
                    value={formData.displayName}
                    onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                    className={errors.displayName ? 'border-destructive' : ''}
                  />
                  {errors.displayName && (
                    <p className="text-sm text-destructive">{errors.displayName}</p>
                  )}
                </div>
              )}

              {(view === 'login' || view === 'signup' || view === 'forgot') && (
                <div className="space-y-2">
                  <Label htmlFor="email">E-post</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="din@epost.se"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className={errors.email ? 'border-destructive' : ''}
                  />
                  {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
                </div>
              )}

              {(view === 'login' || view === 'signup' || view === 'reset') && (
                <div className="space-y-2">
                  <Label htmlFor="password">
                    {view === 'reset' ? 'Nytt lösenord' : 'Lösenord'}
                  </Label>
                  <Input
                    id="password"
                    type="password"
                    placeholder={view === 'login' ? 'Ditt lösenord' : 'Minst 8 tecken'}
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    className={errors.password ? 'border-destructive' : ''}
                  />
                  {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
                </div>
              )}

              {view === 'login' && (
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="rememberMe"
                    checked={formData.rememberMe}
                    onCheckedChange={(checked) =>
                      setFormData({ ...formData, rememberMe: checked === true })
                    }
                  />
                  <Label htmlFor="rememberMe" className="text-sm cursor-pointer">
                    Håll mig inloggad
                  </Label>
                </div>
              )}

              {view === 'reset' && (
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Bekräfta lösenord</Label>
                  <Input
                    id="confirmPassword"
                    type="password"
                    placeholder="Upprepa lösenordet"
                    value={formData.confirmPassword}
                    onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                    className={errors.confirmPassword ? 'border-destructive' : ''}
                  />
                  {errors.confirmPassword && (
                    <p className="text-sm text-destructive">{errors.confirmPassword}</p>
                  )}
                </div>
              )}

              {view === 'signup' && (
                <div className="space-y-2">
                  <div className="flex items-start space-x-2">
                    <Checkbox
                      id="gdprConsent"
                      checked={formData.gdprConsent}
                      onCheckedChange={(checked) =>
                        setFormData({ ...formData, gdprConsent: checked === true })
                      }
                      className={errors.gdprConsent ? 'border-destructive' : ''}
                    />
                    <Label htmlFor="gdprConsent" className="text-sm leading-relaxed">
                      Jag godkänner att mina uppgifter lagras enligt{' '}
                      <a href="/integritetspolicy" className="text-primary underline">
                        integritetspolicyn
                      </a>
                      . Jag förstår att jag kan radera mitt konto när som helst.
                    </Label>
                  </div>
                  {errors.gdprConsent && (
                    <p className="text-sm text-destructive">{errors.gdprConsent}</p>
                  )}
                </div>
              )}

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? 'Vänta...' : config.buttonText}
              </Button>
            </form>

            <div className="mt-6 space-y-3 text-center">
              {view === 'login' && showResendEmail && (
                <div className="bg-muted/50 rounded-lg p-3 space-y-2">
                  <p className="text-sm text-muted-foreground">
                    Har du inte fått bekräftelsemailet?
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleResendConfirmation}
                    disabled={loading}
                  >
                    {loading ? 'Skickar...' : 'Skicka nytt bekräftelsemail'}
                  </Button>
                </div>
              )}

              {view === 'login' && (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setView('forgot');
                      setErrors({});
                    }}
                    className="block w-full text-sm text-muted-foreground hover:text-primary"
                  >
                    Glömt lösenord?
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setView('signup');
                      setErrors({});
                    }}
                    className="text-sm text-muted-foreground hover:text-primary"
                  >
                    Har du inget konto? Skapa ett här
                  </button>
                </>
              )}

              {view === 'signup' && (
                <button
                  type="button"
                  onClick={() => {
                    setView('login');
                    setErrors({});
                  }}
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  Har du redan ett konto? Logga in
                </button>
              )}

              {(view === 'forgot' || view === 'reset') && (
                <button
                  type="button"
                  onClick={() => {
                    setView('login');
                    setErrors({});
                    navigate('/auth');
                  }}
                  className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-primary"
                >
                  <ArrowLeft className="h-4 w-4" />
                  Tillbaka till inloggning
                </button>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      <Footer />
    </div>
  );
}
